/**
 * ChatComposer — mention-aware input for the brief chat panel.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { MentionDropdown } from './MentionDropdown';
import type { MentionItem } from './MentionDropdown';

export interface ChatMention {
  type: string;
  id: string;
  label: string;
}

interface ChatComposerProps {
  disabled?: boolean;
  onSend: (text: string, mentions: ChatMention[]) => void;
  mentionItems?: MentionItem[];
  prefill?: string;
  onPrefillConsumed?: () => void;
  placeholder?: string;
}

export function ChatComposer({
  disabled,
  onSend,
  mentionItems = [],
  prefill,
  onPrefillConsumed,
  placeholder = 'Tell me what to change… (type @ to mention)',
}: ChatComposerProps) {
  const [value, setValue] = useState('');
  const [mentions, setMentions] = useState<ChatMention[]>([]);
  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!prefill) return;
    setValue(prefill);
    setMentions([]);
    setIsMentioning(false);
    setMentionQuery('');
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const pos = prefill.length;
        el.setSelectionRange(pos, pos);
      }
    });
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  const submit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed, mentions);
    setValue('');
    setMentions([]);
    inputRef.current?.focus();
  }, [value, disabled, onSend, mentions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isMentioning && ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    },
    [submit, isMentioning],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      const cursor = e.target.selectionStart ?? val.length;
      setValue(val);

      if (isMentioning) {
        if (mentionStartIndex >= val.length || val[mentionStartIndex] !== '@') {
          setIsMentioning(false);
          setMentionQuery('');
          return;
        }
        const query = val.slice(mentionStartIndex + 1, cursor);
        if (query.includes(' ') && query.trim() === '') {
          setIsMentioning(false);
          setMentionQuery('');
          return;
        }
        setMentionQuery(query);
      } else {
        const charBefore = cursor >= 2 ? val[cursor - 2] : undefined;
        if (
          val[cursor - 1] === '@' &&
          (charBefore === undefined || charBefore === ' ' || charBefore === '\n')
        ) {
          setIsMentioning(true);
          setMentionStartIndex(cursor - 1);
          setMentionQuery('');
        }
      }
    },
    [isMentioning, mentionStartIndex],
  );

  const handleMentionSelect = useCallback(
    (item: MentionItem) => {
      const before = value.slice(0, mentionStartIndex);
      const after = value.slice(inputRef.current?.selectionStart ?? value.length);
      const insertText = `@${item.label} `;
      setValue(before + insertText + after);

      setMentions((prev) => [...prev, { type: item.type, id: item.id, label: item.label }]);
      setIsMentioning(false);
      setMentionQuery('');

      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          const pos = before.length + insertText.length;
          el.focus();
          el.setSelectionRange(pos, pos);
        }
      });
    },
    [value, mentionStartIndex],
  );

  const handleMentionClose = useCallback(() => {
    setIsMentioning(false);
    setMentionQuery('');
  }, []);

  return (
    <div className="border-t border-slate-200 bg-white p-3">
      <div className="relative flex items-end gap-2">
        <MentionDropdown
          items={mentionItems}
          query={mentionQuery}
          onSelect={handleMentionSelect}
          onClose={handleMentionClose}
          visible={isMentioning}
        />

        <textarea
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          rows={2}
          className="flex-1 resize-none text-sm text-slate-800 placeholder:text-slate-400 px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-purple/30 focus:border-brand-purple/50 disabled:bg-slate-50 disabled:text-slate-400"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !value.trim()}
          className="px-4 py-2 text-xs font-semibold rounded-lg bg-brand-purple text-white hover:bg-brand-purple/90 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition"
        >
          Send
        </button>
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">
        Enter to send · Shift+Enter for newline · @ to mention
      </p>
    </div>
  );
}
